import { EvidenceCircleLoader } from '@/components/ui/evidence-circle-loader';
import { cn } from '@/lib/cn';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Translation keys for thinking messages.
 * These are displayed during loading and intentionally generic.
 */
const THINKING_MESSAGE_KEYS: TranslationKey[] = [
  'chat.thinkingMessages.thinking',
  'chat.thinkingMessages.processing',
  'chat.thinkingMessages.analyzing',
  'chat.thinkingMessages.considering',
  'chat.thinkingMessages.contemplatingQuery',
  'chat.thinkingMessages.understandingQuestion',
  'chat.thinkingMessages.interpretingRequest',
  'chat.thinkingMessages.searchingInformation',
  'chat.thinkingMessages.qualifyingSources',
  'chat.thinkingMessages.evaluatingEvidence',
  'chat.thinkingMessages.synthesizingInformation',
  'chat.thinkingMessages.structuringResponse',
  'chat.thinkingMessages.formulatingInsights',
  'chat.thinkingMessages.reviewingClinicalContext',
  'chat.thinkingMessages.weighingEvidence',
];

/** Delay before showing text to avoid flickering during rapid remounts (ms) */
const TEXT_SHOW_DELAY_MS = 500;

/** How long each dot in the ellipsis displays (ms) */
const ELLIPSIS_INTERVAL_MS = 1000;

/** Fade transition duration (ms) */
const FADE_DURATION_MS = 400;

/**
 * Get a random message index, avoiding the previous one if possible.
 * Uses Math.random() which is fine for UI randomization (not security-sensitive).
 */
const getRandomMessageIndex = (prevIndex: number, length: number): number => {
  if (length <= 1) return 0;

  let newIndex: number;
  do {
    newIndex = Math.floor(Math.random() * length); // NOSONAR - UI randomization, not security
  } while (newIndex === prevIndex);

  return newIndex;
};

/**
 * Memoized evidence loader that doesn't re-render when sibling state changes.
 * Uses gooey + wide square formation with soft easing for an organic morph feel.
 */
const AnimatedLoader = memo(function AnimatedLoader() {
  return (
    <EvidenceCircleLoader
      size={28}
      gooey
      spread={1.4}
      formation="square"
      easing="soft"
      className="text-sunlight-400"
    />
  );
});

export interface ChatLoadingIndicatorProps {
  /**
   * Optional pre-resolved phrases to cycle through instead of the generic set.
   *
   * Surfaces can inject context-aware narration (e.g. Haiku-generated phrases
   * derived from the user's query) here. When omitted or empty, the component
   * falls back to the generic {@link THINKING_MESSAGE_KEYS}. Phrases may arrive
   * asynchronously — the indicator fades out, swaps to the new pool at phrase
   * index 0, then fades back in.
   */
  messages?: string[];
}

/**
 * Chat Loading Indicator
 *
 * Displays a loading state with animated Heidi logo and cycling messages
 * while waiting for assistant response.
 *
 * Text is delayed by 500ms to prevent flickering during rapid component remounts.
 *
 * Shown when:
 * - Creating a new chat (isCreatingChat)
 * - Streaming started but no assistant content yet
 * - Extended thinking is in progress
 *
 * Pass {@link ChatLoadingIndicatorProps.messages} to override the generic
 * phrases with surface-specific, context-aware narration.
 */
export const ChatLoadingIndicator = ({
  messages,
}: ChatLoadingIndicatorProps = {}): JSX.Element => {
  const { t } = useTranslations();

  const genericPhrases = useMemo(() => THINKING_MESSAGE_KEYS.map((key) => t(key)), [t]);

  const incomingPhrases = useMemo(
    () => (messages && messages.length > 0 ? messages : genericPhrases),
    [messages, genericPhrases]
  );

  const incomingPoolId = useMemo(
    () => (messages && messages.length > 0 ? messages.join('\0') : 'generic'),
    [messages]
  );

  const [activePhrases, setActivePhrases] = useState(incomingPhrases);
  const [activePoolId, setActivePoolId] = useState(incomingPoolId);
  const phraseCount = activePhrases.length;

  const [showText, setShowText] = useState(false);
  const [messageIndex, setMessageIndex] = useState(
    () => Math.floor(Math.random() * incomingPhrases.length) // NOSONAR - UI randomization, not security
  );
  const [ellipsisCount, setEllipsisCount] = useState(1);
  const [isFading, setIsFading] = useState(false);
  const isMountedRef = useRef(true);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPoolRef = useRef<{ id: string; phrases: string[] } | null>(null);

  const applyPhrasePool = useCallback((phrases: string[], poolId: string) => {
    setActivePhrases(phrases);
    setActivePoolId(poolId);
    setMessageIndex(0);
    // Keep ellipsis progress so the swap does not look like a full remount.
  }, []);

  const clearFadeTimeout = useCallback(() => {
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
  }, []);

  const completeFade = useCallback(() => {
    if (!isMountedRef.current) return;

    const pending = pendingPoolRef.current;
    if (pending) {
      applyPhrasePool(pending.phrases, pending.id);
      pendingPoolRef.current = null;
    } else {
      setMessageIndex((prevIdx) => getRandomMessageIndex(prevIdx, phraseCount));
    }
    setIsFading(false);
  }, [applyPhrasePool, phraseCount]);

  // When a new pool arrives, stash it as pending and let the regular ellipsis
  // cycle apply it at the next fade boundary. Deferring the swap to that seam
  // (instead of firing an off-cycle fade here) means an incoming batch never
  // interrupts the phrase currently on screen — the current phrase plays out,
  // then the next natural transition carries the pool swap. Newer batches
  // overwrite the pending slot (latest-wins), so rapid refreshes coalesce.
  useEffect(() => {
    if (incomingPoolId === activePoolId) return;

    if (!showText) {
      applyPhrasePool(incomingPhrases, incomingPoolId);
      pendingPoolRef.current = null;
      return;
    }

    pendingPoolRef.current = { id: incomingPoolId, phrases: incomingPhrases };
  }, [incomingPoolId, incomingPhrases, activePoolId, showText, applyPhrasePool]);

  // Delay showing text to avoid flickering during rapid remounts
  useEffect(() => {
    isMountedRef.current = true;

    const showTimer = setTimeout(() => {
      if (isMountedRef.current) {
        setShowText(true);
      }
    }, TEXT_SHOW_DELAY_MS);

    return () => {
      isMountedRef.current = false;
      clearTimeout(showTimer);
      clearFadeTimeout();
    };
  }, [clearFadeTimeout]);

  // Animate ellipsis and cycle messages (only starts after text is shown)
  useEffect(() => {
    if (!showText) return;

    const interval = setInterval(() => {
      if (!isMountedRef.current) return;

      setEllipsisCount((prev) => {
        if (prev < 3) {
          return prev + 1;
        }

        setIsFading(true);
        clearFadeTimeout();
        fadeTimeoutRef.current = setTimeout(completeFade, FADE_DURATION_MS);
        return 1;
      });
    }, ELLIPSIS_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      clearFadeTimeout();
    };
  }, [showText, clearFadeTimeout, completeFade]);

  const safeIndex = phraseCount > 0 ? messageIndex % phraseCount : 0;
  const currentMessage = activePhrases[safeIndex] ?? '';
  const ellipsis = '.'.repeat(ellipsisCount);

  return (
    <div className="-ml-1 flex w-full items-center justify-start gap-1">
      <AnimatedLoader />
      {showText && (
        <TypographyP2
          className={cn(
            'min-w-0 flex-1 truncate text-text-secondary transition-opacity duration-300 animate-in fade-in',
            isFading ? 'opacity-0' : 'opacity-100'
          )}
          style={{ transitionDuration: `${FADE_DURATION_MS}ms` }}
        >
          {currentMessage}
          {ellipsis}
        </TypographyP2>
      )}
    </div>
  );
};
